// Éviter la redéclaration de la classe si elle existe déjà
if (typeof window.SizeGuidePicker === 'undefined') {
	window.SizeGuidePicker = class SizeGuidePicker extends HTMLElement {
		constructor() {
			super();
			this.sizeSystems = [];
			this.currentSystem = null;
		}

	connectedCallback() {
		this.loadData();
		this.waitForStylesAndRender();
		this.attachModalListeners();
	}

		loadData() {
			const dataAttr = this.getAttribute('data-size-systems');
			if (!dataAttr) return;

			try {
				const systems = JSON.parse(dataAttr);

				// S'assurer que EU soit toujours en premier
				const euSystemIndex = systems.findIndex((system) => system.name === 'EU');
				if (euSystemIndex > 0) {
					// Déplacer EU en première position
					const euSystem = systems.splice(euSystemIndex, 1)[0];
					systems.unshift(euSystem);
				}

				this.sizeSystems = systems;

				// Le bloc du sélecteur de variantes est re-rendu côté serveur (toujours en EU)
				// à chaque changement de taille : on restaure le système choisi par le client.
				let storedSystem = null;
				try {
					const storedName = window.localStorage.getItem('ax-size-system');
					if (storedName) {
						storedSystem = this.sizeSystems.find((system) => system.name === storedName) || null;
					}
				} catch (e) {
					// localStorage indisponible (navigation privée, cookies bloqués) : on ignore.
				}

				this.currentSystem = storedSystem || this.sizeSystems[0] || null;
			} catch (e) {
				console.error('Erreur parsing JSON guide des tailles:', e);
			}
		}

	waitForStylesAndRender() {
		// Vérifier si les styles sont chargés
		this.ensureStylesLoaded().then(() => {
			this.render();
		});
	}

	ensureStylesLoaded() {
		return new Promise((resolve) => {
			// Vérifier si la feuille de style est déjà chargée
			const styleSheet = Array.from(document.styleSheets).find(sheet => {
				try {
					return sheet.href && sheet.href.includes('ax-sizeguide-picker.css');
				} catch (e) {
					return false;
				}
			});

			if (styleSheet) {
				// Style déjà chargé
				resolve();
			} else {
				// Attendre que le style soit chargé
				const checkStyles = () => {
					const testElement = document.createElement('div');
					testElement.className = 'size-guide-skeleton';
					testElement.style.position = 'absolute';
					testElement.style.visibility = 'hidden';
					document.body.appendChild(testElement);

					const computed = window.getComputedStyle(testElement);
					const hasStyles = computed.animation && computed.animation.includes('skeleton-loading');
					
					document.body.removeChild(testElement);

					if (hasStyles) {
						resolve();
					} else {
						setTimeout(checkStyles, 50);
					}
				};

				checkStyles();
			}
		});
	}

	render() {
		if (!this.sizeSystems.length) {
			this.innerHTML = '';
			return;
		}

		// Skeleton loading - zone unique
		this.innerHTML = '<div class="size-guide-skeleton"></div>';

		// Render actual content après un petit délai pour l'effet skeleton
		setTimeout(() => {
			this.renderNavFromTemplate();
			this.attachEventListeners();

			// Réappliquer la conversion après un re-rendu du bloc, qui revient toujours en EU.
			if (this.currentSystem && this.currentSystem.name !== 'EU') {
				this.updateVariantSizes(this.currentSystem);
			}
			this.forceStyleRecalculation();
		}, 200);
	}

		renderNavFromTemplate() {
			const navTemplate = document.getElementById('size-guide-nav-template');
			const itemTemplate = document.getElementById('size-guide-nav-item-template');

			if (!navTemplate || !itemTemplate) {
				console.error('Templates manquants pour size-guide-picker');
				return;
			}

			// Cloner le template principal
			const navClone = navTemplate.content.cloneNode(true);
			const navContainer = navClone.querySelector('[data-nav-container]');

			// Générer les nav items depuis le template
			this.sizeSystems.forEach((system, index) => {
				const itemClone = itemTemplate.content.cloneNode(true);
				const radio = itemClone.querySelector('input[type="radio"]');
				const label = itemClone.querySelector('label');

				// IDs uniques
				const radioId = `size-system-${system.name.toLowerCase()}-${Date.now()}-${index}`;

				radio.id = radioId;
				radio.value = system.name;
				label.setAttribute('for', radioId);
				label.textContent = system.name;

				// Système courant sélectionné (EU par défaut, ou celui mémorisé)
				if (this.currentSystem ? system.name === this.currentSystem.name : index === 0) {
					radio.checked = true;
				}

				navContainer.appendChild(itemClone);
			});

		// Remplacer le contenu
		this.innerHTML = '';
		this.appendChild(navClone);
		
		// Forcer le recalcul des styles après injection
		this.forceStyleRecalculation();
		}

		attachEventListeners() {
			const radios = this.querySelectorAll('input[type="radio"][name="size-system"]');

			radios.forEach((radio) => {
				radio.addEventListener('change', (e) => {
					if (e.target.checked) {
						this.handleSystemChange(e.target.value);
					}
				});
			});
		}

	handleSystemChange(systemName) {
		const systemData = this.sizeSystems.find((system) => system.name === systemName);
		if (!systemData) return;

		this.currentSystem = systemData;

		// Mémoriser le choix : le bloc est re-rendu en EU à chaque changement de taille.
		try {
			window.localStorage.setItem('ax-size-system', systemData.name);
		} catch (e) {
			// localStorage indisponible : le choix ne survivra pas au re-rendu.
		}

		// Ajouter une classe de mise à jour pour feedback visuel
		const variantPickers = document.querySelectorAll('variant-picker');
		variantPickers.forEach((picker) => picker.classList.add('size-guide-updating'));

		// Update variant sizes
		this.updateVariantSizes(systemData);

		// Retirer la classe après un délai pour permettre la transition
		setTimeout(() => {
			variantPickers.forEach((picker) => picker.classList.remove('size-guide-updating'));
		}, 300);

		// Dispatch custom event
		this.dispatchEvent(
			new CustomEvent('size-system-changed', {
				detail: { system: systemData },
				bubbles: true,
			}),
		);
	}

		updateVariantSizes(systemData) {
			// Trouver le variant picker et ses options de taille
			// Tous les sélecteurs de variantes de la page (il peut y en avoir plusieurs)
			const variantPickers = document.querySelectorAll('variant-picker');
			if (!variantPickers.length) return;

			// Trouver tous les spans avec data-original-size (tailles EU de base)
			const sizeSpans = [];
			variantPickers.forEach((picker) => {
				picker.querySelectorAll('span[data-original-size]').forEach((span) => sizeSpans.push(span));
			});

			// Créer un mapping EU -> autres systèmes
			const euSizes = this.sizeSystems.find((system) => system.name === 'EU')?.lines || [];
			const targetSizes = systemData.lines;

			sizeSpans.forEach((sizeSpan) => {
				const originalSize = sizeSpan.getAttribute('data-original-size');
				if (!originalSize) return;

				// Trouver l'index de cette taille dans le système EU
				const euIndex = euSizes.findIndex((euSize) => {
					// Comparaison flexible pour gérer les formats différents
					const cleanOriginal = originalSize.trim().replace(/\s+/g, ' ');
					const cleanEu = euSize.trim().replace(/\s+/g, ' ');
					return cleanOriginal === cleanEu;
				});

				if (euIndex !== -1 && targetSizes[euIndex]) {
					const newSize = targetSizes[euIndex];

					// Toujours simplifier en span simple avec juste le texte
					// Peu importe la structure originale (rich text ou pas)
					sizeSpan.innerHTML = '';
					sizeSpan.textContent = newSize;

					// S'assurer que le data-original-size est préservé
					sizeSpan.setAttribute('data-original-size', originalSize);
				}
			});
			
			// Forcer le recalcul des styles après mise à jour des tailles
			this.forceStyleRecalculation();
		}

		attachModalListeners() {
			// Bouton d'ouverture de la modal
			const triggerButton = document.querySelector('[data-size-guide-trigger]');
			if (triggerButton) {
				triggerButton.addEventListener('click', () => this.openModal());
			}
		}

		openModal() {
			const modal = document.querySelector('[data-size-guide-modal]');
			if (!modal) return;

			// Générer le tableau si pas encore fait
			this.generateTable();

			// Ouvrir la modal avec l'API x-modal
			modal.show();
		}

		closeModal() {
			const modal = document.querySelector('[data-size-guide-modal]');
			if (!modal) return;

			// Fermer la modal avec l'API x-modal
			modal.hide();
		}

		generateTable() {
			const tableContainer = document.querySelector('[data-size-guide-table-container]');
			if (!tableContainer || !this.sizeSystems.length) return;

			// Ne pas régénérer si déjà présent
			if (tableContainer.querySelector('.size-guide-table')) return;

			// Créer le tableau
			const table = document.createElement('table');
			table.className = 'size-guide-table';

			// En-tête du tableau
			const thead = document.createElement('thead');
			const headerRow = document.createElement('tr');

			// Une colonne pour chaque système de tailles
			this.sizeSystems.forEach((system) => {
				const th = document.createElement('th');
				th.textContent = system.name;
				headerRow.appendChild(th);
			});

			thead.appendChild(headerRow);
			table.appendChild(thead);

			// Corps du tableau
			const tbody = document.createElement('tbody');

			// Déterminer le nombre maximum de tailles
			const maxSizes = Math.max(...this.sizeSystems.map((system) => system.lines.length));

			// Créer une ligne pour chaque taille
			for (let i = 0; i < maxSizes; i++) {
				const row = document.createElement('tr');

				// Une cellule pour chaque système
				this.sizeSystems.forEach((system) => {
					const td = document.createElement('td');
					td.textContent = system.lines[i] || '-';
					row.appendChild(td);
				});

				tbody.appendChild(row);
			}

			table.appendChild(tbody);
			tableContainer.appendChild(table);

			console.log(
				'Tableau des tailles généré avec',
				maxSizes,
				'lignes et',
				this.sizeSystems.length,
				'systèmes',
			);
		}

		forceStyleRecalculation() {
			// Forcer le navigateur à recalculer les styles
			// Cette méthode force un reflow/repaint
			if (this.offsetHeight !== undefined) {
				// Déclencher un reflow en accédant à une propriété de layout
				this.style.transform = 'translateZ(0)';
				
				// Forcer également le recalcul sur les éléments enfants
				const childElements = this.querySelectorAll('*');
				childElements.forEach(el => {
					if (el.offsetHeight !== undefined) {
						el.style.transform = 'translateZ(0)';
					}
				});
				
				// Utiliser requestAnimationFrame pour s'assurer que les styles sont appliqués
				requestAnimationFrame(() => {
					this.style.transform = '';
					
					// Restaurer les transforms des enfants
					childElements.forEach(el => {
						el.style.transform = '';
					});
					
					// Déclencher un event customisé pour notifier que les styles sont prêts
					this.dispatchEvent(
						new CustomEvent('styles-applied', {
							detail: { component: 'size-guide-picker' },
							bubbles: true,
						}),
					);
				});
			}
		}
	}; // Fin de la classe SizeGuidePicker
}

// Register the component
if (!customElements.get('size-guide-picker')) {
	customElements.define('size-guide-picker', window.SizeGuidePicker || SizeGuidePicker);
}
